import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

const DRAFT_PREFIX = 'draft:';
const PERSIST_DEBOUNCE_MS = 500;
/** An abandoned draft is noise after this long, and stops the store growing forever. */
const MAX_DRAFT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredDraft<T> {
	savedAt: number;
	values: T;
}

const forget = (storageKey: string) => {
	try {
		window.localStorage.removeItem(storageKey);
	} catch {
		// storage disabled — nothing to clean up
	}
};

const readDraft = <T>(storageKey: string): T | null => {
	let stored: string | null = null;

	try {
		stored = window.localStorage.getItem(storageKey);
	} catch {
		return null; // storage disabled (private mode, blocked cookies)
	}

	if (!stored) return null;

	try {
		const parsed = JSON.parse(stored) as StoredDraft<T>;

		// a corrupt or pre-versioning entry is worse than none
		if (!parsed || typeof parsed.savedAt !== 'number' || !parsed.values) {
			forget(storageKey);
			return null;
		}
		if (Date.now() - parsed.savedAt > MAX_DRAFT_AGE_MS) {
			forget(storageKey);
			return null;
		}

		return parsed.values;
	} catch {
		forget(storageKey);
		return null;
	}
};

/**
 * Drops every stored draft. Call this wherever the session identity changes —
 * logout, and the sysadmin "log in as user" paths, which swap the jwt without
 * logging out. Without it the next person at a shared browser restores the
 * previous one's form content, which for order and ticket forms means tenant
 * names and addresses.
 */
export const clearAllFormDrafts = (): void => {
	try {
		const keys: string[] = [];
		for (let i = 0; i < window.localStorage.length; i++) {
			const key = window.localStorage.key(i);
			if (key?.startsWith(DRAFT_PREFIX)) keys.push(key);
		}
		keys.forEach((key) => window.localStorage.removeItem(key));
	} catch {
		// storage disabled — nothing to clean up
	}
};

type OwnerId = number | string | null;

interface DraftState<T> {
	key: string;
	ownerId: OwnerId;
	values: T;
	restored: boolean;
	/** False once the user has actually edited something. */
	pristine: boolean;
}

const buildStorageKey = (ownerId: OwnerId, key: string) => `${DRAFT_PREFIX}${ownerId}:${key}`;

const initState = <T extends object>(
	ownerId: OwnerId,
	key: string,
	defaultValues: T
): DraftState<T> => {
	const restored = ownerId === null ? null : readDraft<T>(buildStorageKey(ownerId, key));

	return {
		key,
		ownerId,
		// a draft written before a field existed must not leave it undefined
		values: { ...defaultValues, ...(restored ?? {}) },
		restored: restored !== null,
		pristine: true
	};
};

interface FormDraft<T> {
	values: T;
	setValues: Dispatch<SetStateAction<T>>;
	/** True when the initial values came from storage, so the UI can say so. */
	hasRestoredDraft: boolean;
	/** Call from the success path of a submit — never before the request settles. */
	clearDraft: () => void;
}

/**
 * Owns a form's state and mirrors it into localStorage, so a dropped
 * connection, an accidental reload or a session timeout does not cost the user
 * their work. This repo has no form library — forms are `useState` plus
 * `ValidatorForm` — so the hook holds the state itself and the caller binds
 * inputs to `values` / `setValues` as usual.
 *
 * Only worth it for long or high-value forms. The caller clears the draft once
 * the request has succeeded — never at submit start, or a failed request would
 * discard exactly the input this exists to protect.
 *
 * `ownerId` is the current user id (from `state.auth.user`). It namespaces the
 * entry so one person never restores another's input; pass `null` while the
 * user is unknown and persistence is skipped entirely. Namespace `key` with
 * the entity id (`order-42`, not `order`) so two edit sessions of different
 * records cannot overwrite each other.
 */
export const useFormDraft = <T extends object>(
	ownerId: OwnerId,
	key: string,
	defaultValues: T
): FormDraft<T> => {
	const enabled = ownerId !== null && ownerId !== undefined;
	const storageKey = buildStorageKey(ownerId, key);

	const [state, setState] = useState<DraftState<T>>(() => initState(ownerId, key, defaultValues));

	if (state.key !== key) {
		// A different record (order-42 → order-43). Without this the old record's
		// values would be persisted under the new key and destroy its real draft.
		setState(initState(ownerId, key, defaultValues));
	} else if (state.ownerId !== ownerId) {
		// Callers pass null while auth is still rehydrating, so owner null → id is
		// the same session becoming known, not a different person. Re-initialising
		// there would discard input already typed — the exact loss this prevents.
		const authJustResolved = state.ownerId === null && ownerId !== null;

		setState((prev) =>
			authJustResolved && !prev.pristine
				? { ...prev, ownerId }
				: initState(ownerId, key, defaultValues)
		);
	}

	const setValues = useCallback<Dispatch<SetStateAction<T>>>((update) => {
		setState((prev) => ({
			...prev,
			pristine: false,
			values: typeof update === 'function' ? (update as (p: T) => T)(prev.values) : update
		}));
	}, []);

	const clearDraft = useCallback(() => forget(storageKey), [storageKey]);

	useEffect(() => {
		// Writing while untouched would store the defaults, and every later mount
		// would then claim it restored input the user never typed.
		if (state.pristine || !enabled) return;

		const timeoutId = setTimeout(() => {
			try {
				const payload: StoredDraft<T> = { savedAt: Date.now(), values: state.values };
				window.localStorage.setItem(storageKey, JSON.stringify(payload));
			} catch {
				// quota exceeded or storage disabled — drafting is best effort
			}
		}, PERSIST_DEBOUNCE_MS);

		return () => clearTimeout(timeoutId);
	}, [state.pristine, state.values, storageKey, enabled]);

	return {
		values: state.values,
		setValues,
		hasRestoredDraft: state.restored,
		clearDraft
	};
};
